import type { ComponentPropsWithoutRef, MouseEvent } from "react";
import { useCallback, useState } from "react";

import { type Account } from "../../../../hooks/useAccountList";
import { isEllipsisActive } from "../../../NavigationBar/utils/helpers";
import type { AccountNode } from "../../AccountSwitcherContext";
import { useAccountSwitcherContext } from "../../AccountSwitcherContext";
import { Icon } from "../../SwitcherIcon";

import {
  StyledWrapper,
  StyledAccountName,
  StyledExpander,
  StyledLogo,
  StyledAccountNameWrapper,
  StyledCheckMark,
} from "./styled";

import "@nectary/components/tooltip";

export interface AccountSwitcherNodeProps
  extends ComponentPropsWithoutRef<"div"> {
  index: number;
  isExpand: boolean;
  accountName: Account["accountName"];
  accountId: Account["accountId"];
  subAccounts?: Account["children"];
  depthLevel?: number;
}

export const AccountSwitcherNode: React.FC<AccountSwitcherNodeProps> = ({
  index,
  isExpand,
  accountId,
  accountName,
  subAccounts = [],
  depthLevel = 0,
  ...props
}) => {
  const {
    hasAtLeastOneAccountHasChildren,
    onAccountChange,
    currentAccount,
    setList,
    list,
  } = useAccountSwitcherContext();
  const [expand, setExpand] = useState<boolean>(isExpand);
  const [isOverflow, setOverflow] = useState<boolean>(false);

  const onTextMounted = useCallback((el: HTMLSpanElement | null) => {
    if (el) {
      setOverflow(isEllipsisActive(el));
    }
  }, []);

  const onAccountClick = useCallback(
    (e: MouseEvent<HTMLDivElement>) => {
      e.preventDefault();
      onAccountChange?.({
        accountId,
        accountName,
      });
    },
    [onAccountChange, accountId, accountName]
  );

  const toggleExpand = useCallback(
    () => () => {
      const children: AccountNode[] = subAccounts.map((account) => ({
        level: depthLevel + 1,
        isExpand: false,
        account,
      }));

      if (!isExpand) {
        list.splice(index + 1, 0, ...children);
      } else {
        let deleteItems = 0;
        let i = index + 1;
        while (i < list.length && list?.[i]?.level !== list?.[index]?.level) {
          deleteItems++;
          i++;
        }
        list.splice(index + 1, deleteItems);
      }

      list[index] = {
        ...(list[index] as AccountNode),
        isExpand: !list?.[index]?.isExpand,
      };

      setList([...list]);
      setExpand((prev) => !prev);
    },
    [setList, subAccounts, isExpand, list, index, depthLevel]
  );

  const isDisabled = !subAccounts.length;
  /* istanbul ignore next */
  const isActive = currentAccount?.accountId === accountId;
  const accountText = accountName || accountId;

  const Node = (
    <StyledWrapper {...props}>
      <StyledAccountNameWrapper
        $depth={depthLevel}
        $hasChildren={!!subAccounts.length}
        $hasAtLeastOneAccountHasChildren={!!hasAtLeastOneAccountHasChildren}
      >
        <StyledExpander
          role="button"
          onClick={toggleExpand()}
          $disable={isDisabled}
          $isExpand={expand}
        >
          {!isDisabled && <Icon name="chevron-right" />}
        </StyledExpander>
        <StyledAccountName onClick={onAccountClick}>
          <StyledLogo
            size="sm"
            accountId={accountId}
            accountName={accountName}
          />
          <span ref={onTextMounted}>{accountText}</span>
          {isActive && (
            <StyledCheckMark>
              <Icon name="check" />
            </StyledCheckMark>
          )}
        </StyledAccountName>
      </StyledAccountNameWrapper>
    </StyledWrapper>
  );

  if (isOverflow) {
    return (
      <sinch-tooltip text={accountText} orientation={"top"}>
        {Node}
      </sinch-tooltip>
    );
  }

  return Node;
};
